// Auto-layout for the workflow designer.
//
// PURE ON PURPOSE. It takes a graph as plain data and returns geometry — no React, no DOM, no state. Two
// things depend on that. The designer draws a definition with it, and the read-only progress view draws a
// running instance's FROZEN graph with the very same function, so a case is always rendered by the code that
// drew the workflow it is executing. Building the runtime view separately would guarantee the two drift.
//
// Positions are COMPUTED, never hand-placed. An admin cannot drag a node into a misleading position, cannot
// leave a connector dangling, and cannot produce the spaghetti a free canvas invites. The trade is that the
// layout has to be good on its own — which is what the refinement passes below are for.
//
// The shape it produces is a vertical spine: each node sits one row below its deepest parent, branches fan out
// into side-by-side columns, and a Join pulls them back to centre.

export const NODE_W = 248;
export const NODE_H = 78;
export const H_GAP = 36;
export const V_GAP = 66;
export const PAD = 32;

/** Node types that a graph is built from. Mirrors WorkflowVocabulary on the server. */
export const NODE_TYPES = ["Start", "Approval", "Acknowledge", "Decision", "Join", "End"];

const keyOf = (n) => n && (n.nodeKey ?? n.NodeKey);
const fromOf = (e) => e && (e.fromNodeKey ?? e.FromNodeKey);
const toOf = (e) => e && (e.toNodeKey ?? e.ToNodeKey);

/**
 * Detects edges that point back to a node already on the current DFS stack — a rework loop ("send it back for
 * correction"). They are excluded from depth computation, because a back-edge is not a step forward and would
 * otherwise push its target below the node it returns to and turn the whole spine inside out. They are drawn
 * afterwards as a side loop.
 */
export function findBackEdges(nodes, edges) {
  const out = new Map();
  (edges || []).forEach((e) => {
    const f = fromOf(e);
    if (!out.has(f)) out.set(f, []);
    out.get(f).push(e);
  });

  const back = new Set();
  const state = new Map(); // 0 = unseen, 1 = on stack, 2 = done

  const visit = (key) => {
    state.set(key, 1);
    (out.get(key) || []).forEach((e) => {
      const t = toOf(e);
      const s = state.get(t) || 0;
      if (s === 1) back.add(e);
      else if (s === 0) visit(t);
    });
    state.set(key, 2);
  };

  (nodes || []).forEach((n) => {
    if (!state.get(keyOf(n))) visit(keyOf(n));
  });

  return back;
}

/**
 * Longest-path depth over forward edges only. Longest rather than shortest so a Join always sits BELOW every
 * branch it merges — with shortest paths a join would float up next to the fastest branch and the merge would
 * read as if it happened early.
 */
function computeDepths(nodes, edges, backEdges) {
  const depth = new Map();
  (nodes || []).forEach((n) => depth.set(keyOf(n), 0));

  const forward = (edges || []).filter((e) => !backEdges.has(e));

  // Relax repeatedly; bounded by node count, which is tiny here (a workflow is tens of nodes, not thousands).
  for (let pass = 0; pass < (nodes || []).length + 1; pass++) {
    let changed = false;
    forward.forEach((e) => {
      const f = depth.get(fromOf(e));
      const t = depth.get(toOf(e));
      if (f === undefined || t === undefined) return;
      if (t < f + 1) {
        depth.set(toOf(e), f + 1);
        changed = true;
      }
    });
    if (!changed) break;
  }

  return depth;
}

const median = (values) => {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/**
 * Lays a workflow graph out.
 *
 * @returns {{positions: Object, rows: Array, connectors: Array, width: number, height: number, backEdges: Array}}
 */
export function layoutWorkflow(nodes, edges) {
  const safeNodes = (nodes || []).filter((n) => keyOf(n));
  const safeEdges = (edges || []).filter((e) => fromOf(e) && toOf(e));

  if (!safeNodes.length) {
    return { positions: {}, rows: [], connectors: [], width: NODE_W + PAD * 2, height: PAD * 2, backEdges: [] };
  }

  const backEdges = findBackEdges(safeNodes, safeEdges);
  const depth = computeDepths(safeNodes, safeEdges, backEdges);

  // Group into rows by depth, preserving declaration order as a stable tie-break so the layout does not
  // reshuffle between renders of the same graph.
  const rows = [];
  safeNodes.forEach((n) => {
    const d = depth.get(keyOf(n)) || 0;
    if (!rows[d]) rows[d] = [];
    rows[d].push(n);
  });
  for (let i = 0; i < rows.length; i++) if (!rows[i]) rows[i] = [];

  // Parent / child maps over forward edges, used by the centring passes.
  const parentsOf = new Map();
  const childrenOf = new Map();
  safeEdges.forEach((e) => {
    if (backEdges.has(e)) return;
    if (!childrenOf.has(fromOf(e))) childrenOf.set(fromOf(e), []);
    childrenOf.get(fromOf(e)).push(toOf(e));
    if (!parentsOf.has(toOf(e))) parentsOf.set(toOf(e), []);
    parentsOf.get(toOf(e)).push(fromOf(e));
  });

  // Initial x: evenly spaced slots within each row.
  const x = new Map();
  rows.forEach((row) => {
    row.forEach((n, i) => x.set(keyOf(n), i * (NODE_W + H_GAP)));
  });

  // Refine: pull each node toward the median of its children, then of its parents. Two directions because a
  // Decision wants to sit over its branches while a Join wants to sit under the branches it merges; doing only
  // one leaves whichever it is off-centre. A handful of passes is plenty at this scale.
  for (let pass = 0; pass < 4; pass++) {
    for (let d = rows.length - 1; d >= 0; d--) {
      rows[d].forEach((n) => {
        const kids = (childrenOf.get(keyOf(n)) || []).map((k) => x.get(k)).filter((v) => v !== undefined);
        const m = median(kids);
        if (m !== null) x.set(keyOf(n), m);
      });
      separateRow(rows[d], x);
    }
    for (let d = 0; d < rows.length; d++) {
      rows[d].forEach((n) => {
        const ps = (parentsOf.get(keyOf(n)) || []).map((k) => x.get(k)).filter((v) => v !== undefined);
        const m = median(ps);
        if (m !== null && (parentsOf.get(keyOf(n)) || []).length > 1) x.set(keyOf(n), m);
      });
      separateRow(rows[d], x);
    }
  }

  // Normalise so the leftmost node sits at the padding, and centre single-node rows on the widest row.
  let minX = Infinity;
  let maxX = -Infinity;
  safeNodes.forEach((n) => {
    const v = x.get(keyOf(n));
    minX = Math.min(minX, v);
    maxX = Math.max(maxX, v);
  });
  const shift = PAD - minX;
  const width = maxX - minX + NODE_W + PAD * 2;

  const positions = {};
  rows.forEach((row, d) => {
    row.forEach((n) => {
      positions[keyOf(n)] = {
        x: x.get(keyOf(n)) + shift,
        y: PAD + d * (NODE_H + V_GAP),
        w: NODE_W,
        h: NODE_H,
        depth: d,
        node: n,
      };
    });
  });

  const height = PAD * 2 + rows.length * NODE_H + Math.max(0, rows.length - 1) * V_GAP;

  const connectors = safeEdges
    .map((e) => buildConnector(e, positions, backEdges.has(e), width))
    .filter(Boolean);

  return { positions, rows, connectors, width, height, backEdges: [...backEdges] };
}

/** Pushes nodes apart within a row so refinement can never overlap two boxes. */
function separateRow(row, x) {
  if (!row || row.length < 2) return;
  const ordered = [...row].sort((a, b) => x.get(keyOf(a)) - x.get(keyOf(b)));
  for (let i = 1; i < ordered.length; i++) {
    const prev = x.get(keyOf(ordered[i - 1]));
    const cur = x.get(keyOf(ordered[i]));
    const min = prev + NODE_W + H_GAP;
    if (cur < min) x.set(keyOf(ordered[i]), min);
  }
}

/**
 * Orthogonal connector: straight down where the columns line up, otherwise down, across the gap, and down
 * again. Right angles rather than curves because a branch that jogs at a visible corner is easier to follow
 * with your eye than several beziers overlapping near a join.
 */
function buildConnector(edge, positions, isBack, canvasWidth) {
  const a = positions[fromOf(edge)];
  const b = positions[toOf(edge)];
  if (!a || !b) return null;

  const ax = a.x + a.w / 2;
  const ay = a.y + a.h;
  const bx = b.x + b.w / 2;
  const by = b.y;

  if (isBack) {
    // A rework loop runs out to the right margin and back up, so it never lies on top of the forward spine.
    const lane = Math.min(canvasWidth - 12, Math.max(a.x + a.w, b.x + b.w) + H_GAP);
    const path = [
      `M ${a.x + a.w} ${a.y + a.h / 2}`,
      `H ${lane}`,
      `V ${b.y + b.h / 2}`,
      `H ${b.x + b.w}`,
    ].join(" ");
    return { edge, path, isBack, labelX: lane, labelY: (a.y + b.y) / 2 + NODE_H / 2 };
  }

  const midY = ay + (by - ay) / 2;
  const path = Math.abs(ax - bx) < 1
    ? `M ${ax} ${ay} V ${by}`
    : `M ${ax} ${ay} V ${midY} H ${bx} V ${by}`;

  return { edge, path, isBack: false, labelX: (ax + bx) / 2, labelY: midY - 6 };
}

/**
 * Where a "+" insert button belongs on each forward connector. Inserting between two nodes is the primary way
 * a graph is built here — there is no palette-drag-to-canvas step, because the layout decides position anyway.
 */
export function insertPoints(layout) {
  return (layout.connectors || [])
    .filter((c) => !c.isBack)
    .map((c) => {
      const a = layout.positions[fromOf(c.edge)];
      const b = layout.positions[toOf(c.edge)];
      if (!a || !b) return null;
      return {
        edge: c.edge,
        x: (a.x + a.w / 2 + b.x + b.w / 2) / 2,
        y: (a.y + a.h + b.y) / 2,
      };
    })
    .filter(Boolean);
}

/**
 * Overlays live instance state onto a laid-out graph: which nodes are done, active, or were never taken.
 * Used only by the read-only progress view — the designer passes no tasks and every node renders neutral.
 */
export function applyInstanceState(layout, { activeNodeKeys = [], tasks = [] } = {}) {
  const active = new Set((activeNodeKeys || []).filter(Boolean));
  const byNode = new Map();
  (tasks || []).forEach((t) => {
    const k = t.nodeKey ?? t.NodeKey;
    if (!byNode.has(k)) byNode.set(k, []);
    byNode.get(k).push(t);
  });

  const states = {};
  Object.keys(layout.positions || {}).forEach((key) => {
    const ts = byNode.get(key) || [];
    const done = ts.some((t) => (t.status ?? t.Status) === "Completed");
    const rejected = ts.some((t) => (t.decision ?? t.Decision) === "Reject");
    const skipped = ts.length > 0 && ts.every((t) => ["Skipped", "Cancelled"].includes(t.status ?? t.Status));

    states[key] = active.has(key)
      ? "active"
      : rejected
      ? "rejected"
      : done
      ? "done"
      : skipped
      ? "skipped"
      : "pending";
  });

  return states;
}
